'use client'
import { Quiz } from '@/components/pages/quiz';
import { Results } from '@/components/pages/results';
import { CocktailDetail } from '@/components/pages/cocktail';
import { Error } from '@/components/pages/error';
import { StoreProvider } from './AppHooks';
import { TLocaleData, TResponse, TStore } from '@/shared/types';
import { useEffect, useReducer, useState } from 'react';
import { storeProductsToFp } from '@/shared/appInit';
import { checkBrowser, getConsumerId, GetSessionStorageItem, getSessionToken, getUrlQuery } from '@/shared/utils';
import { setCookieItem } from '@/shared/cookie';
import { redirect } from 'next/navigation';
import { Home } from '@/components/pages/home';
import { Landing } from './pages/landing';
import { usePathname } from 'next/navigation';
import { CocktailDreamerDetailTanqueray } from './pages/dreamer';
import { styleName } from '@/config/const';
import { HttpRequest } from '@/shared/request';
import { CMSAPI } from '@/config/api';
import AppLoader from './AppLoader';
import ScreenIdleTimer from './ScreenIdleTimer';
import { getPublicEnvConfig } from '@/config/env';
import { AgeGate } from './AgeGate';
const dynamicComponents = {
  home: Home,
  cocktailDetail: CocktailDetail,
  quiz: Quiz,
  results: Results,
  error: Error,
  landingTanqueray: Landing,
  cocktailDreamerDetailTanqueray: CocktailDreamerDetailTanqueray,
}
export default function AppMain(props: {
  localeData: TLocaleData
  pageComponents: Array<{
    contentType: string;
    [key: string]: any;
  }>
  locale: string
}) {
  const { localeData, pageComponents, locale } = props
  const { defaultStore } = localeData;
  const [isClient, setIsClient] = useState(false)
  const [searchParams, setSearchParams] = useState({ queryIsReady: false })
  function paramsHandler() {
    setIsClient(true)
    setSearchParams({ ...Object.fromEntries(new URLSearchParams(location.search)), queryIsReady: true })
    const noCookie = getUrlQuery('noCookie')
    if (noCookie) {
      setCookieItem('OptanonAlertBoxClosed', new Date().toISOString(), {
        days: 180,
        path: '/'
      });
    } else {
      document.body.classList.remove('no-cookie-banner');
    }
  }
  const [_storeConfig, setStoreConfig] = useReducer(
    (state: TStore, newState: TStore) => ({ ...state, ...newState }),
    defaultStore as any
  );
  useEffect(() => {
    checkBrowser();
    getConsumerId();
    getSessionToken();
    paramsHandler();
    window.addEventListener('beforeunload', () => {
      getSessionToken(true)
    })
    return () => {
      window.removeEventListener('beforeunload', () => {
        getSessionToken(true)
      })
    }
  }, [])
  const pathname = usePathname();
  useEffect(() => {
    // setTimeout(() => {
    //   window.scrollTo({ top: 0, behavior: 'smooth' });
    // }, 0)
    const storage = window?.sessionStorage
    if (!storage) return
    const currentPath = sessionStorage.getItem('currentPath')
    if (currentPath === pathname) return
    const prevPath = sessionStorage.getItem('currentPath')
    storage.setItem('prevPath', prevPath as string)
    storage.setItem('currentPath', window.location.pathname)
  }, [pathname])
  const [loading, setLoading] = useState(true)
  useEffect(() => {
    if (!isClient) return
    if (location.pathname.includes('/error') || !defaultStore.id) {
      setLoading(false)
      return
    }
    const { DEFAULTLOCALE } = getPublicEnvConfig()
    if (!defaultStore.storeEnable) {
      return redirect(`/${DEFAULTLOCALE}/home${location.search}`);
    }
    const token = getUrlQuery('token')
    const expireTime = 60 * 1000 * 5;//5 min
    const cacheStoreConfig = GetSessionStorageItem('storeConfig', `${locale}_${token || defaultStore.id}`, expireTime)
    if (cacheStoreConfig) {
      setStoreConfig({ ...cacheStoreConfig, isReady: true })
      storeProductsToFp(cacheStoreConfig)
      setTimeout(() => {
        setLoading(false)
      }, 0)
      return
    } else {
      sessionStorage.removeItem('fp_init')
    }
    HttpRequest.post<TResponse<Array<TStore>>>(`${CMSAPI}`, {
      locale,
      contentType: 'storeList',
      title: token || defaultStore.id,
      unselect_refer_fields: ['cocktailList'],
    }).then(res => {
      if (res.data.success) {
        const urlStoreConfig = res.data?.data?.filter(store => store.storeEnable).sort((a, b) => a.updateDate - b.updateDate)[0]
        if (!urlStoreConfig) {
          if (token) {
            if (token === defaultStore.id) {
              return redirect(`/${DEFAULTLOCALE}/home${location.search}`);
            }
            return redirect(`/${locale}/error?type=tokenNotFound_${token}`);
          } else {
            return redirect(`/${DEFAULTLOCALE}/home${location.search}`);
          }
        }
        setStoreConfig({ ...urlStoreConfig, isReady: true })
        storeProductsToFp(urlStoreConfig)
        setTimeout(() => {
          setLoading(false)
        }, 0)
      }
    })
  }, [defaultStore?.id, isClient])
  // if (!_storeConfig) {
  //   return redirect(`/${locale}/error`);
  // }
  return (
    <StoreProvider value={{
      storeConfig: {
        ..._storeConfig,
        storeStyleName: _storeConfig.storeStyleName ?? styleName.Master,
      }, locale, localeData
    }}>
      <main role='main' className={_storeConfig.storeStyleName?.toLowerCase().replace(' ', '-')}>
        {pageComponents?.map((pageComponent, index) => {
          const Component = dynamicComponents[pageComponent.contentType as keyof typeof dynamicComponents];
          if (!Component) return <h1 className='text-center font-title' key={`not-found-${pageComponent.contentType}`}>{`This component '${pageComponent.contentType}' needs to be created.`}</h1>;
          return (
            <Component key={index + pageComponent.contentType} {...pageComponent as any} searchParams={searchParams} />
          )
        })}
        <AgeGate />
      </main>
      <ScreenIdleTimer />
      {loading && <AppLoader />}
    </StoreProvider>
  )
}